import {
  PropertiesContext,
  PropertiesContextType,
} from '@/contexts/PropertiesContext'
import {
  Box,
  Button,
  HStack,
  Progress,
  Step,
  StepDescription,
  StepIcon,
  StepIndicator,
  StepNumber,
  Stepper,
  StepSeparator,
  StepStatus,
  StepTitle,
  Text,
  VStack,
} from '@chakra-ui/react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useContext, useMemo } from 'react'
import { FormProvider } from 'react-hook-form'
import { usePropertyFormRHF } from '../../../../hooks/usePropertyFormRHF'
import type { Property } from '../../../../types'
import {
  AccessInstructionsStep,
  BasicInfoStep,
  CostsManagementStep,
  ImagesStep,
  PropertyDetailsStep,
} from './steps'

interface PropertyFormRHFProps {
  property?: Property | null
  onClose: () => void
}

export function PropertyFormRHF({ property, onClose }: PropertyFormRHFProps) {
  const { selectedStaysProperty, setSelectedStaysProperty } = useContext<
    PropertiesContextType | undefined
  >(PropertiesContext) as PropertiesContextType
  const {
    form,
    control,
    handleSubmit: hookHandleSubmit,
    onSubmit,
    steps,
    // errors,
    currentStep,
    goToStep,
    nextStep,
    prevStep,
    validateStep,
    areRequiredStepsValid,
    isLoading,
  } = usePropertyFormRHF({
    property,
    st_property: selectedStaysProperty,
    onSuccess: onClose,
  })

  const { handleSubmit, ...hooForm } = form

  const canSubmit = useMemo(
    () => areRequiredStepsValid(),
    [areRequiredStepsValid]
  )

  // console.log({ errors })

  const currentStepComponent = useMemo(() => {
    switch (currentStep) {
      case 0:
        return <BasicInfoStep control={control} />
      case 1:
        return <PropertyDetailsStep control={control} />
      case 2:
        return <AccessInstructionsStep control={control} />
      case 3:
        return <CostsManagementStep control={control} />
      case 4:
        return <ImagesStep control={control} />
      default:
        return null
    }
  }, [currentStep, control])

  const handleNext = async () => {
    const isValid = await validateStep(currentStep)
    if (isValid || !steps[currentStep].required) {
      nextStep()
    }
  }

  const handleStepClick = async (stepIndex: number) => {
    // Allow navigation to any step if current step is valid or optional
    const isCurrentStepValid = await validateStep(currentStep)
    if (isCurrentStepValid || !steps[currentStep].required) {
      goToStep(stepIndex)
    }
  }

  const progress = ((currentStep + 1) / steps.length) * 100

  // Prevent Enter key from submitting form unless on last step
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && currentStep < steps.length - 1) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  return (
    <FormProvider handleSubmit={hookHandleSubmit} {...hooForm}>
      <VStack
        spacing={8}
        as="form"
        onSubmit={hookHandleSubmit(onSubmit)}
        onKeyDown={handleKeyDown}
        noValidate
      >
        {/* Header */}
        <VStack spacing={4} align="stretch" w="full">
          {/* Progress Bar */}
          <Box>
            <Progress
              value={progress}
              colorScheme="blue"
              size="sm"
              borderRadius="md"
            />
            <Text fontSize="sm" color="gray.600" mt={1}>
              Passo {currentStep + 1} de {steps.length}
            </Text>
          </Box>
        </VStack>

        {/* Body */}
        <VStack spacing={6} align="stretch" w="full">
          {/* Stepper */}
          <Stepper index={currentStep} orientation="horizontal" size="sm">
            {steps.map((step, index) => (
              <Step key={index} onClick={() => handleStepClick(index)}>
                <StepIndicator cursor="pointer">
                  <StepStatus
                    complete={<StepIcon />}
                    incomplete={<StepNumber />}
                    active={<StepNumber />}
                  />
                </StepIndicator>

                <Box flexShrink="0" cursor="pointer">
                  <StepTitle fontSize="sm">{step.title}</StepTitle>
                  <StepDescription fontSize="xs">
                    {step.description}
                  </StepDescription>
                </Box>

                <StepSeparator />
              </Step>
            ))}
          </Stepper>

          {/* Current Step Content */}
          <Box minH="400px">{currentStepComponent}</Box>
        </VStack>

        {/* Footer */}
        <HStack spacing={3} w="full" justify="space-between">
          {/* Previous Button */}
          <Button
            type="button"
            leftIcon={<ChevronLeft size={16} />}
            onClick={prevStep}
            isDisabled={currentStep === 0}
            variant="outline"
          >
            Anterior
          </Button>

          <HStack spacing={3}>
            {/* Skip Button (for optional steps) */}
            {!steps[currentStep].required && currentStep < steps.length - 1 && (
              <Button type="button" variant="ghost" onClick={nextStep}>
                Pular
              </Button>
            )}

            {/* Next Button (only show if not on last step) */}
            {currentStep < steps.length - 1 && (
              <Button
                type="button"
                rightIcon={<ChevronRight size={16} />}
                onClick={handleNext}
                colorScheme="blue"
              >
                Próximo
              </Button>
            )}

            {/* Submit Button (show when all required steps are valid) */}
            {canSubmit && (
              <Button
                type="submit"
                colorScheme="green"
                isLoading={isLoading}
                loadingText="Salvando..."
                isDisabled={isLoading}
              >
                {property ? 'Atualizar' : 'Criar'} Acomodação
              </Button>
            )}

            <Button
              type="button"
              onClick={() => {
                setSelectedStaysProperty(null)
                onClose()
              }}
              variant="ghost"
            >
              Cancelar
            </Button>
          </HStack>
        </HStack>
      </VStack>
    </FormProvider>
  )
}
